import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api, UserData } from '../services/api';

interface UserDataContextType {
    userData: UserData | null;
    loading: boolean;
    error: string | null;
    refreshUserData: () => Promise<void>;
    updateLanguage: (language: string) => Promise<void>;
    setUserLanguage: (language: string) => Promise<void>;
    addMemorizedSentence: (en: string, hi: string, category: string) => Promise<void>;

    addFavoriteSentence: (itemId: string, wordCount: number) => Promise<void>;
    addFavoriteWord: (itemId: string, difficulty: string) => Promise<void>;
    addCompletedSentence: (itemId: string, wordCount: number) => Promise<void>;
    addCompletedWord: (itemId: string, difficulty: string) => Promise<void>;
    addCompletedGrammar: (itemId: string, topic: string) => Promise<void>;
    updateGrammarProgress: (grammarProgress: UserData['grammarProgress']) => Promise<void>;
    // Favorite grammar
    addFavoriteGrammar: (itemId: string, topic: string) => Promise<void>;
    removeFavoriteGrammar: (itemId: string) => Promise<void>;
    getFavoriteGrammar: (topic: string) => Promise<any[]>;
    getFavoriteSentences: (wordCount: number) => Promise<any[]>;
    getFavoriteWords: (difficulty: string) => Promise<any[]>;
    // Add these:
    removeFavoriteSentence: (itemId: string) => Promise<void>;
    removeFavoriteWord: (itemId: string) => Promise<void>;
    getCompletedSentences: (wordCount: number) => Promise<any[]>;
    getCompletedWords: (difficulty: string) => Promise<any[]>;
    getCompletedGrammar: (topic: string) => Promise<any[]>;
    clearStoredLanguagePreference: () => void;
}

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export const useUserData = () => {
    const context = useContext(UserDataContext);
    if (context === undefined) {
        throw new Error('useUserData must be used within a UserDataProvider');
    }
    return context;
};

interface UserDataProviderProps {
    children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
    const { currentUser } = useAuth();
    const [userData, setUserData] = useState<UserData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refreshUserData = async () => {
        if (!currentUser?.uid) return;

        console.log('UserDataContext - refreshUserData called for:', currentUser.uid);
        setLoading(true);
        setError(null);
        try {
            const data = await api.getUserData(currentUser.uid);
            console.log('UserDataContext - user data loaded:', data);
            setUserData(data);
        } catch (err) {
            console.error('Error loading user data:', err);
            // Fallback: check localStorage for language selection
            const storedLanguage = localStorage.getItem('selectedLanguage');
            if (storedLanguage) {
                console.log('Using localStorage fallback for user data');
                const tempUserData: UserData = {
                    userId: currentUser.uid,
                    selectedLanguage: storedLanguage,
                    memorizedSentences: [],
                    favoriteSentences: [],
                    favoriteWords: [],
                    completedSentences: [],
                    completedWords: [],
                    grammarProgress: {
                        partsOfSpeech: {},
                        tenses: {}
                    }
                };
                setUserData(tempUserData);
                setError(null);
            } else {
                // Default to English for new users
                console.log('No stored language preference, defaulting to English');
                const tempUserData: UserData = {
                    userId: currentUser.uid,
                    selectedLanguage: 'en',
                    memorizedSentences: [],
                    favoriteSentences: [],
                    favoriteWords: [],
                    completedSentences: [],
                    completedWords: [],
                    grammarProgress: {
                        partsOfSpeech: {},
                        tenses: {}
                    }
                };
                setUserData(tempUserData);
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const updateLanguage = async (language: string) => {
        if (!currentUser?.uid) {
            console.error('No current user found');
            return;
        }

        console.log('Updating language for user:', currentUser.uid, 'to:', language);
        try {
            await api.updateUserLanguage(currentUser.uid, language);
            console.log('Language updated in API');
            await refreshUserData();
            console.log('User data refreshed');
        } catch (err) {
            console.error('Error updating language:', err);
            // Fallback: store in localStorage temporarily
            console.log('Using localStorage fallback for language selection');
            localStorage.setItem('selectedLanguage', language);
            // Create a temporary userData object
            const tempUserData: UserData = {
                userId: currentUser.uid,
                selectedLanguage: language,
                memorizedSentences: [],
                favoriteSentences: [],
                favoriteWords: [],
                completedSentences: [],
                completedWords: [],
                grammarProgress: {
                    partsOfSpeech: {},
                    tenses: {}
                }
            };
            setUserData(tempUserData);
            setError(null);
        }
    };

    // Function to clear stored language preferences for new users
    const clearStoredLanguagePreference = () => {
        localStorage.removeItem('selectedLanguage');
    };

    const addMemorizedSentence = async (en: string, hi: string, category: string) => {
        if (!currentUser?.uid) return;

        try {
            await api.addMemorizedSentence(currentUser.uid, en, hi, category);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add memorized sentence');
        }
    };



    // Add favorite sentence (NEW: use favorite collection)
    const addFavoriteSentence = async (itemId: string, wordCount: number) => {
        if (!currentUser?.uid) return;
        try {
            await api.addFavoriteSentence(currentUser.uid, itemId, wordCount);
            await refreshUserData();
        } catch (err: any) {
            if (err?.message && err.message.includes('Already favorited')) {
                await refreshUserData();
                return;
            }
            setError(err instanceof Error ? err.message : 'Failed to add favorite sentence');
        }
    };

    // Add favorite word (NEW: use favorite collection)
    const addFavoriteWord = async (itemId: string, difficulty: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.addFavoriteWord(currentUser.uid, itemId, difficulty);
            await refreshUserData();
        } catch (err: any) {
            if (err?.message && err.message.includes('Already favorited')) {
                await refreshUserData();
                return;
            }
            setError(err instanceof Error ? err.message : 'Failed to add favorite word');
        }
    };

    // Add completed sentence (NEW: use completed collection)
    const addCompletedSentence = async (itemId: string, wordCount: number) => {
        console.log('currentUser--->', currentUser)
        console.log('itemId--->', itemId)
        if (!currentUser?.uid) return;
        try {
            await api.addCompletedSentence(currentUser.uid, itemId, wordCount);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add completed sentence');
        }
    };

    // Add completed word (NEW: use completed collection)
    const addCompletedWord = async (itemId: string, difficulty: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.addCompletedWord(currentUser.uid, itemId, difficulty);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add completed word');
        }
    };

    // Add completed grammar (NEW: use completed collection)
    const addCompletedGrammar = async (itemId: string, topic: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.addCompletedGrammar(currentUser.uid, itemId, topic);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add completed grammar');
        }
    };

    const updateGrammarProgress = async (grammarProgress: UserData['grammarProgress']) => {
        if (!currentUser?.uid) return;

        try {
            await api.updateGrammarProgress(currentUser.uid, grammarProgress);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update grammar progress');
        }
    };

    // Add favorite grammar
    const addFavoriteGrammar = async (itemId: string, topic: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.addFavoriteGrammar(currentUser.uid, itemId, topic);
            await refreshUserData();
        } catch (err: any) {
            if (err?.message && err.message.includes('Already favorited')) {
                await refreshUserData();
                return;
            }
            setError(err instanceof Error ? err.message : 'Failed to add favorite grammar');
        }
    };
    // Remove favorite grammar
    const removeFavoriteGrammar = async (itemId: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.removeFavoriteGrammar(currentUser.uid, itemId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove favorite grammar');
        }
    };
    // Get favorite grammar for a topic
    const getFavoriteGrammar = async (topic: string) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getFavoriteGrammar(currentUser.uid, topic);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get favorite grammar');
            return [];
        }
    };

    const getFavoriteSentences = async (wordCount: number) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getFavoriteSentences(currentUser.uid, wordCount);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get favorite sentences');
            return [];
        }
    };

    const getFavoriteWords = async (difficulty: string) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getFavoriteWords(currentUser.uid, difficulty);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get favorite words');
            return [];
        }
    };

    // Remove favorite sentence
    const removeFavoriteSentence = async (itemId: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.removeFavoriteSentence(currentUser.uid, itemId);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove favorite sentence');
        }
    };
    // Remove favorite word
    const removeFavoriteWord = async (itemId: string) => {
        if (!currentUser?.uid) return;
        try {
            await api.removeFavoriteWord(currentUser.uid, itemId);
            await refreshUserData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove favorite word');
        }
    };

    const getCompletedSentences = async (wordCount: number) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getCompletedSentences(currentUser.uid, wordCount);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get completed sentences');
            return [];
        }
    };

    const getCompletedWords = async (difficulty: string) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getCompletedWords(currentUser.uid, difficulty);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get completed words');
            return [];
        }
    };

    const getCompletedGrammar = async (topic: string) => {
        if (!currentUser?.uid) return [];
        try {
            return await api.getCompletedGrammar(currentUser.uid, topic);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get completed grammar');
            return [];
        }
    };

    useEffect(() => {
        console.log('UserDataContext - currentUser changed:', currentUser?.uid);
        if (currentUser?.uid) {
            console.log('UserDataContext - refreshing user data for:', currentUser.uid);
            refreshUserData();
        } else {
            console.log('UserDataContext - clearing user data');
            setUserData(null);
        }
    }, [currentUser?.uid]);

    const value: UserDataContextType = {
        userData,
        loading,
        error,
        refreshUserData,
        updateLanguage,
        setUserLanguage: updateLanguage,
        addMemorizedSentence,

        addFavoriteSentence,
        addFavoriteWord,
        addCompletedSentence,
        addCompletedWord,
        addCompletedGrammar,
        updateGrammarProgress,
        addFavoriteGrammar,
        removeFavoriteGrammar,
        getFavoriteGrammar,
        getFavoriteSentences,
        getFavoriteWords,
        removeFavoriteSentence,
        removeFavoriteWord,
        getCompletedSentences,
        getCompletedWords,
        getCompletedGrammar,
        clearStoredLanguagePreference,
    };

    return (
        <UserDataContext.Provider value={value}>
            {children}
        </UserDataContext.Provider>
    );
}; 